import React from 'react';
import { Flex, Text, Heading, Box, Stack, Icon, Badge, useColorModeValue } from '@chakra-ui/react';
import { TriangleUpIcon, TimeIcon } from '@chakra-ui/icons';
import { FaCalendarAlt } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { Trans } from '@lingui/macro';

interface Transaction {
  period: string;
  vehicleCount: number;
  amount: string;
}

interface MonthlyTransactionsCardProps {
  title: string;
  today: Transaction;
  lastSevenDays: Transaction;
  thisMonth: Transaction;
  percentageChange: string;
}

interface TransactionItemProps {
  icon: IconType | React.ComponentType;
  label: string;
  count: number;
  amount: string;
  labelColor: string;
  valueColor: string;
  accentColor?: string;
}

// Helper component for transaction items
const TransactionItem: React.FC<TransactionItemProps> = ({
  icon,
  label,
  count,
  amount,
  labelColor,
  valueColor,
  accentColor = 'blue.500',
}) => {
  return (
    <Flex justify="space-between" align="center">
      <Flex align="center">
        <Icon as={icon} color={accentColor} mr={2} />
        <Text color={labelColor} fontWeight="medium">
          {label}
        </Text>
      </Flex>
      <Flex align="center" fontWeight="bold" color={valueColor}>
        <Text mr={2}>
          {count} <Trans>Araç</Trans>,
        </Text>
        <Text>{amount}</Text>
      </Flex>
    </Flex>
  );
};

export const MonthlyTransactionsCard: React.FC<MonthlyTransactionsCardProps> = ({
  title,
  today,
  lastSevenDays,
  thisMonth,
  percentageChange,
}) => {
  const headingColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'white');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box height="100%" width="100%" display="flex" flexDirection="column" minHeight="300px">
      <Flex mb={4} align="center">
        <Icon as={TriangleUpIcon} color={accentColor} mr={2} boxSize={5} />
        <Heading size="md" color={headingColor}>
          {title}
        </Heading>
      </Flex>

      <Box
        flex="1"
        bg={bgColor}
        borderWidth="1px"
        borderRadius="lg"
        borderColor={borderColor}
        position="relative"
        overflow="hidden"
        boxShadow="sm"
        p={5}
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          bg: accentColor,
        }}
      >
        <Stack spacing={5}>
          <TransactionItem
            icon={TimeIcon}
            label={today.period}
            count={today.vehicleCount}
            amount={today.amount}
            labelColor={labelColor}
            valueColor={valueColor}
            accentColor={accentColor}
          />

          <TransactionItem
            icon={FaCalendarAlt}
            label={lastSevenDays.period}
            count={lastSevenDays.vehicleCount}
            amount={lastSevenDays.amount}
            labelColor={labelColor}
            valueColor={valueColor}
            accentColor="purple.500"
          />

          <TransactionItem
            icon={FaCalendarAlt}
            label={thisMonth.period}
            count={thisMonth.vehicleCount}
            amount={thisMonth.amount}
            labelColor={labelColor}
            valueColor={valueColor}
            accentColor="teal.500"
          />

          <Flex justify="space-between" align="center" mt={2} py={3} borderTopWidth="1px" borderColor={borderColor}>
            <Text fontWeight="medium" color={labelColor}>
              <Trans>Geçen Aya Göre</Trans>
            </Text>
            <Badge
              colorScheme={Number(percentageChange) >= 0 ? 'green' : 'red'}
              fontSize="md"
              fontWeight="bold"
              px={2}
              py={1}
              borderRadius="md"
            >
              {Number(percentageChange) >= 0 ? '+' : ''}
              {percentageChange}%
            </Badge>
          </Flex>
        </Stack>
      </Box>
    </Box>
  );
};
